import type { PollStats } from "../hooks/usePollInsights";

interface DashboardProps {
  stats: PollStats | null;
  loading: boolean;
  error: string | null;
}

export function Dashboard({ stats, loading, error }: DashboardProps) {
  if (loading) {
    return (
      <div className="dashboard-panel loading-state">
        <div className="loader"></div>
        <p>Loading on-chain intelligence...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-panel error-state">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="dashboard-panel empty-state">
        <p>No insights yet. Create a ballot to begin streaming metrics.</p>
      </div>
    );
  }

  const decryptedRatio = stats.totalPolls
    ? Math.min(100, Math.round((stats.decryptedPolls / stats.totalPolls) * 100))
    : 0;
  const activeRatio = stats.totalPolls
    ? Math.min(100, Math.round((stats.activePolls / stats.totalPolls) * 100))
    : 0;

  return (
    <div className="dashboard-panel">
      <div className="dashboard-grid">
        <article className="stat-card accent-one">
          <p className="dashboard-stat-label">Total Polls</p>
          <p className="dashboard-stat-value">{stats.totalPolls}</p>
          <p className="dashboard-stat-hint">Encrypted cases recorded</p>
        </article>

        <article className="stat-card accent-two">
          <p className="dashboard-stat-label">Active Polls</p>
          <p className="dashboard-stat-value">{stats.activePolls}</p>
          <p className="dashboard-stat-hint">{activeRatio}% awaiting decryption</p>
        </article>

        <article className="stat-card accent-three">
          <p className="dashboard-stat-label">Decrypted Polls</p>
          <p className="dashboard-stat-value">{stats.decryptedPolls}</p>
          <p className="dashboard-stat-hint">{decryptedRatio}% decrypted</p>
        </article>

        <article className="stat-card accent-four">
          <p className="dashboard-stat-label">Total Voters</p>
          <p className="dashboard-stat-value">{stats.totalVoters}</p>
          <p className="dashboard-stat-hint">Secure signatures collected</p>
        </article>

        <article className="stat-card accent-five">
          <p className="dashboard-stat-label">Avg Voters/Poll</p>
          <p className="dashboard-stat-value">{stats.averageVoters}</p>
          <p className="dashboard-stat-hint">Participation level per seal</p>
        </article>

        <article className="stat-card accent-six">
          <p className="dashboard-stat-label">Turnout Ratio</p>
          <p className="dashboard-stat-value">{stats.totalPolls ? `${(stats.totalVoters / (stats.totalPolls || 1)).toFixed(1)} votes` : "0 votes"}</p>
          <p className="dashboard-stat-hint">{stats.totalPolls ? `${Math.min(100, Math.round((stats.totalVoters / (stats.totalPolls || 1)) * 10))}% avg` : "Waiting"}</p>
        </article>
      </div>

      <div className="dashboard-progress-group">
        <div className="progress-block">
          <div className="progress-label">
            <span>Decryption momentum</span>
            <span>{decryptedRatio}% completed</span>
          </div>
          <div className="progress-track">
            <span className="progress-fill" style={{ width: `${decryptedRatio}%` }} />
          </div>
        </div>

        <div className="progress-block">
          <div className="progress-label">
            <span>Active ballots in flight</span>
            <span>{activeRatio}%</span>
          </div>
          <div className="progress-track">
            <span className="progress-fill" style={{ width: `${activeRatio}%` }} />
          </div>
        </div>
      </div>

      {stats.highlight && (
        <div className="highlight-card">
          <p className="highlight-kicker">Latest decrypted poll</p>
          <h3>{stats.highlight.title}</h3>
          <p className="highlight-winner">
            Winner: <span>{stats.highlight.winner}</span> with{" "}
            <strong>{Math.round(stats.highlight.share * 100)}%</strong> of the counted votes
          </p>
          <div className="highlight-details">
            <span>{stats.highlight.votes} votes decrypted</span>
            <span>Polled at {stats.highlight.timestamp}</span>
          </div>
        </div>
      )}
    </div>
  );
}

